import { FC, ReactNode } from "react";

import {
  Box,
  Row,
  SystemStyleObject,
  Text,
  TextProps,
  Tooltip,
  TooltipProps,
} from "@hightouch/htui";

import { useContentFits } from "src/hooks/use-content-fits";

export type TextWithTooltipProps = TextProps &
  Omit<TooltipProps, "message"> & {
    message?: ReactNode;
    /** Always show the tooltip, bypassing the content-fits check. */
    forceTooltip?: boolean;
    sx?: SystemStyleObject;
  };

export const TextWithTooltip: FC<Readonly<TextWithTooltipProps>> = ({
  children,
  isDisabled = false,
  forceTooltip = false,
  keyboardShortcut,
  message,
  placement,
  sx,
  ...props
}) => {
  const { contentFits, ref } = useContentFits<HTMLDivElement>();

  return (
    <>
      {/* Remove the `width: max-content` in the span to make ellipsis show */}
      <Row
        display="flex"
        alignItems="center"
        overflow="hidden"
        sx={{ ">span": { width: "100%", whiteSpace: "nowrap", ...sx } }}
      >
        <Tooltip
          message={message ?? String(children)}
          isDisabled={(!forceTooltip && contentFits) || isDisabled}
          keyboardShortcut={keyboardShortcut}
          placement={placement}
        >
          {/*
           * The Tooltip owns the ref of its direct child: it clones the child with
           * its own forwarded ref in both the disabled (`cloneElement(child, { ref })`)
           * and enabled (Chakra merge-refs) branches. That ref ends up pointing at
           * tooltip-internal nodes, never reliably at the text we need to measure, so
           * placing the content-fits ref on the tooltip child does not work. Instead
           * the tooltip trigger is this wrapper Box, leaving the inner Text free to
           * hold a stable content-fits ref that always measures the truncated text.
           */}
          <Box minWidth={0} overflow="hidden" width="100%">
            {/* Set display to block to make ellipsis show */}
            <Text ref={ref} isTruncated display="block" {...props}>
              {children}
            </Text>
          </Box>
        </Tooltip>
      </Row>
    </>
  );
};
